import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// EST is the primary portal timezone for all assignment scheduling.
export const EST_TIMEZONE = "America/New_York";

/**
 * Normalize a timezone value, falling back to EST when missing/invalid.
 */
export const normalizeTimezone = (tz, fallback = EST_TIMEZONE) => {
  if (!tz || typeof tz !== "string") return fallback;
  const trimmed = tz.trim();
  if (!trimmed) return fallback;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: trimmed }).format();
    return trimmed;
  } catch {
    return fallback;
  }
};

/**
 * Short abbreviation for a timezone at a given date, e.g. "EDT", "PDT", "CET".
 */
export const getTimezoneAbbreviation = (tz, date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: normalizeTimezone(tz),
      timeZoneName: "short",
    }).formatToParts(date);
    return (
      parts.find((part) => part.type === "timeZoneName")?.value ||
      String(tz)
    );
  } catch {
    return String(tz);
  }
};

/**
 * Long label for a timezone, e.g. "Eastern Daylight Time".
 */
export const getTimezoneLabel = (tz) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: normalizeTimezone(tz),
      timeZoneName: "long",
    }).formatToParts(new Date());
    return (
      parts.find((part) => part.type === "timeZoneName")?.value || String(tz)
    );
  } catch {
    return String(tz);
  }
};

/**
 * Convert an EST wall-clock time ("HH:mm:ss" or "HH:mm") on a given date
 * to the display time in another zone. Returns "h:mm A" or null when invalid.
 */
export const formatTimeInZone = (timeStr, date, tz) => {
  if (!timeStr) return null;
  const targetTz = normalizeTimezone(tz);
  try {
    const dateValue = date
      ? dayjs(date).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    const [hours, minutes = "0", seconds = "0"] = String(timeStr)
      .split(":")
      .map((part) => Number(part));
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    const estMoment = dayjs.tz(
      `${dateValue} ${hours}:${minutes}:${seconds}`,
      "YYYY-MM-DD HH:mm:ss",
      EST_TIMEZONE,
    );
    return estMoment.tz(targetTz).format("h:mm A");
  } catch {
    return null;
  }
};

/**
 * Collect unique non-EST timezones present on an assignment's Client/Linguist.
 */
export const getAssignmentTimezones = (assignmentData) => {
  const timezones = [];
  const push = (tz) => {
    const normalized = normalizeTimezone(tz);
    if (normalized !== EST_TIMEZONE && !timezones.includes(normalized)) {
      timezones.push(normalized);
    }
  };

  if (assignmentData?.Client?.Timezone) {
    push(assignmentData.Client.Timezone);
  }
  if (assignmentData?.Linguist?.Timezone) {
    push(assignmentData.Linguist.Timezone);
  }
  if (Array.isArray(assignmentData?.Linguists)) {
    assignmentData.Linguists.forEach((linguist) => {
      if (linguist?.Timezone) push(linguist.Timezone);
    });
  }
  return timezones;
};

/**
 * Build multi-timezone display lines for a time range:
 *   ["10:00 AM - 11:00 AM EST",
 *    "Client: 7:00 AM - 8:00 AM PDT",
 *    "Linguist: 4:00 PM - 5:00 PM CET"]
 * EST is always the first line; only non-EST zones are appended.
 */
export const buildMultiTimezoneLines = ({
  timeStart,
  timeEnd,
  date,
  clientTimezone,
  linguistTimezones = [],
}) => {
  const lines = [];

  const formatRange = (tz) => {
    const start = formatTimeInZone(timeStart, date, tz);
    const end = formatTimeInZone(timeEnd, date, tz);
    const abbreviation = getTimezoneAbbreviation(
      tz,
      date ? dayjs(date).toDate() : new Date(),
    );
    return `${start || "?"} - ${end || "?"} ${abbreviation}`;
  };

  lines.push(formatRange(EST_TIMEZONE));

  const clientTz = clientTimezone
    ? normalizeTimezone(clientTimezone)
    : EST_TIMEZONE;
  if (clientTz !== EST_TIMEZONE) {
    lines.push(`Client: ${formatRange(clientTz)}`);
  }

  [...new Set(linguistTimezones.map(normalizeTimezone))]
    .filter((tz) => tz !== EST_TIMEZONE)
    .forEach((tz) => {
      lines.push(`Linguist: ${formatRange(tz)}`);
    });

  return lines;
};

/**
 * Format a single time for all relevant zones:
 *   "10:00 AM EST / Client: 7:00 AM PDT / Linguist: 4:00 PM CET"
 * Returns null when the time is missing/invalid.
 */
export const formatTimeMultiTimezone = (
  timeStr,
  date,
  { clientTimezone, linguistTimezones = [] } = {},
) => {
  const primary = formatTimeInZone(timeStr, date, EST_TIMEZONE);
  if (!primary) return null;

  const parts = [`${primary} EST`];

  const clientTz = clientTimezone
    ? normalizeTimezone(clientTimezone)
    : EST_TIMEZONE;
  if (clientTz !== EST_TIMEZONE) {
    const clientTime = formatTimeInZone(timeStr, date, clientTz);
    if (clientTime) {
      parts.push(
        `Client: ${clientTime} ${getTimezoneAbbreviation(clientTz, date ? dayjs(date).toDate() : new Date())}`,
      );
    }
  }

  [...new Set(linguistTimezones.map(normalizeTimezone))]
    .filter((tz) => tz !== EST_TIMEZONE)
    .forEach((tz) => {
      const linguistTime = formatTimeInZone(timeStr, date, tz);
      if (linguistTime) {
        parts.push(
          `Linguist: ${linguistTime} ${getTimezoneAbbreviation(tz, date ? dayjs(date).toDate() : new Date())}`,
        );
      }
    });

  return parts.join(" / ");
};

export default {
  EST_TIMEZONE,
  normalizeTimezone,
  getTimezoneAbbreviation,
  getTimezoneLabel,
  formatTimeInZone,
  getAssignmentTimezones,
  buildMultiTimezoneLines,
  formatTimeMultiTimezone,
};
